import { Handler } from "../types/store-types";
import { toast } from "react-toastify";

import {
    IAuthState,
    AuthActionTypes,
    IUserTypes,
    SignInSuccessActionType,
    SignUpSuccessActionType,
    SetLoadingActionType,
    SignInRequestActionType,
    SignUpRequestActionType,
    SignInFailureActionType,
    SignUpFailureActionType,
    ClearErrorsActionType,
    InitUserRequestActionType,
    SetUserTokenActionType,
    InitUserSuccessActionType,
    InitUserFailureActionType,
    ConfirmUserRequestActionType,
    ConfirmUserSuccessActionType,
    ConfirmUserFailureActionType,
    ForgotPasswordRequestActionType,
    ForgotPasswordSuccessActionType,
    ForgotPasswordFailureActionType,
    GoogleLoginRequestActionType,
    GoogleLoginSuccessActionType,
    GoogleLoginFailureActionType,
    SetRedirectUrlActionType,
    OAuthFlowRequestActionType,
    OAuthFlowSuccessActionType,
    OAuthFlowFailureActionType,
} from "./types";
import GetUrl from "../../hooks/GetUrl";

/* -- Init User Request Handler -- */

const initUserRequest: Handler<IAuthState, InitUserRequestActionType> = state => {
    return { ...state, isLoading: true };
};

const initUserSuccess: Handler<IAuthState, InitUserSuccessActionType> = (
    state,
    { payload: { token, refresh_token, userType } },
) => {
    return {
        ...state,
        isLoading: false,
        userType,
        token,
        refresh_token,
        errors: null,
    };
};

const initUserFailure: Handler<IAuthState, InitUserFailureActionType> = (state, { payload }) => {
    return { ...state, isLoading: false, errors: payload };
};

/* -- Sign In Request Handler -- */

const signInRequest: Handler<IAuthState, SignInRequestActionType> = state => {
    return { ...state, isLoading: true };
};

const signInSuccess: Handler<IAuthState, SignInSuccessActionType> = (
    state,
    { payload: { token, refresh_token, userType } },
) => {
    if (state.redirectUrl) {
        return {
            ...state,
            isLoading: false,
            userType,
            token,
            refresh_token,
            errors: null,
        };
    } else {
        const path = GetUrl();
        window.parent.postMessage(
            {
                token,
                refresh_token,
                userType,
            },
            path,
        );

        return {
            ...state,
            isLoading: false,
            userType: IUserTypes.USER,
            token: "",
            refresh_token: "",
        };
    }
};

const signInFailure: Handler<IAuthState, SignInFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");
    return { ...state, errors: payload, isLoading: false };
};

/* -- Sign Up Request Handler -- */

const signUpRequest: Handler<IAuthState, SignUpRequestActionType> = state => {
    return { ...state, isLoading: true };
};

const sighUpSuccess: Handler<IAuthState, SignUpSuccessActionType> = (state, { payload }) => {
    window.location.pathname = "";

    return { ...state, user: payload, isLoading: false, errors: null };
};

const signUpFailure: Handler<IAuthState, SignUpFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");

    return { ...state, errors: payload, isLoading: false };
};

/* -- Confirm User Request Handler -- */

const confirmUserRequest: Handler<IAuthState, ConfirmUserRequestActionType> = state => {
    return { ...state };
};

const confirmUserSuccess: Handler<IAuthState, ConfirmUserSuccessActionType> = state => {
    return { ...state };
};

const confirmUserFailure: Handler<IAuthState, ConfirmUserFailureActionType> = state => {
    return { ...state };
};

const setUserToken: Handler<IAuthState, SetUserTokenActionType> = (state, { payload: { token } }) => {
    return { ...state, token };
};

const setLoading: Handler<IAuthState, SetLoadingActionType> = (state, { payload: { isLoading } }) => {
    return { ...state, isLoading };
};

const clearErrors: Handler<IAuthState, ClearErrorsActionType> = state => {
    return { ...state, errors: null };
};

const setRedirectUrl: Handler<IAuthState, SetRedirectUrlActionType> = (
    state,
    { payload: { redirectUrl, redirectUrlQueries, isOAuth } },
) => {
    return { ...state, redirectUrl, redirectUrlQueries, isOAuth };
};

/* -- Forgot Password Request Handler -- */

const forgotPasswordRequest: Handler<IAuthState, ForgotPasswordRequestActionType> = state => {
    return { ...state };
};

const forgotPasswordSuccess: Handler<IAuthState, ForgotPasswordSuccessActionType> = state => {
    return {
        ...state,
    };
};

const forgotPasswordFailure: Handler<IAuthState, ForgotPasswordFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");
    return { ...state, errors: payload };
};

/* Google */

const googleLoginRequest: Handler<IAuthState, GoogleLoginRequestActionType> = (state, { payload }) => {
    const path = GetUrl();
    window.parent.postMessage("googleAuth", path);
    return { ...state };
};
const googleLoginSuccess: Handler<IAuthState, GoogleLoginSuccessActionType> = (state, { payload }) => {
    const path = GetUrl();
    window.parent.postMessage(
        {
            token: payload.access_token,
            refresh_token: payload.refresh_token,
        },
        path,
    );
    return { ...state };
};
const googleLoginFailure: Handler<IAuthState, GoogleLoginFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");
    return { ...state };
};

/* OAuth */

const oauthFlowRequest: Handler<IAuthState, OAuthFlowRequestActionType> = (state, { payload }) => {
    return { ...state, isLoading: true };
};

const oauthFlowSuccess: Handler<IAuthState, OAuthFlowSuccessActionType> = (state, { payload }) => {
    const { url } = payload;
    window.location.href = url;
    return { ...state, isLoading: false };
};

const oauthFlowFailure: Handler<IAuthState, OAuthFlowFailureActionType> = (state, { payload }) => {
    toast.error(payload.data ? payload.data.message : "Something went wrong");
    return { ...state };
};

export const authHandlers = {
    [AuthActionTypes.INIT_USER_REQUEST]: initUserRequest,
    [AuthActionTypes.INIT_USER_SUCCESS]: initUserSuccess,
    [AuthActionTypes.INIT_USER_FAILURE]: initUserFailure,

    [AuthActionTypes.SIGN_IN_REQUEST]: signInRequest,
    [AuthActionTypes.SIGN_IN_SUCCESS]: signInSuccess,
    [AuthActionTypes.SIGN_IN_FAILURE]: signInFailure,

    [AuthActionTypes.SIGN_UP_REQUEST]: signUpRequest,
    [AuthActionTypes.SIGN_UP_SUCCESS]: sighUpSuccess,
    [AuthActionTypes.SIGN_UP_FAILURE]: signUpFailure,

    [AuthActionTypes.CONFIRM_USER_REQUEST]: confirmUserRequest,
    [AuthActionTypes.CONFIRM_USER_SUCCESS]: confirmUserSuccess,
    [AuthActionTypes.CONFIRM_USER_FAILURE]: confirmUserFailure,

    [AuthActionTypes.FORGOT_PASSWORD_REQUEST]: forgotPasswordRequest,
    [AuthActionTypes.FORGOT_PASSWORD_SUCCESS]: forgotPasswordSuccess,
    [AuthActionTypes.FORGOT_PASSWORD_FAILURE]: forgotPasswordFailure,

    [AuthActionTypes.SET_USER_TOKEN]: setUserToken,
    [AuthActionTypes.SET_LOADING]: setLoading,
    [AuthActionTypes.CLEAR_ERRORS]: clearErrors,

    [AuthActionTypes.SET_REDIRECT_URL]: setRedirectUrl,

    [AuthActionTypes.GOOGLE_LOGIN_REQUEST]: googleLoginRequest,
    [AuthActionTypes.GOOGLE_LOGIN_SUCCESS]: googleLoginSuccess,
    [AuthActionTypes.GOOGLE_LOGIN_FAILURE]: googleLoginFailure,

    [AuthActionTypes.OATUH_FLOW_REQUEST]: oauthFlowRequest,
    [AuthActionTypes.OATUH_FLOW_SUCCESS]: oauthFlowSuccess,
    [AuthActionTypes.OATUH_FLOW_FAILURE]: oauthFlowFailure,
};
